import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["videoId", "onReady", "onPlay", "onPause", "onSeek", "onBackward", "onForward", "onTimeUpdate", "onFullscreen", "onSpeedChange", "onQualityChange", "onError", "nextVideo", "videoAnalyticsData"];
import { forwardRef, useEffect, useRef } from 'react';
import CrehanaPlayerClass from "../CrehanaPlayerClass";
import { DEFAULT_VIDEO_ENDING_DURATION, VIDEO_LOAD_TIMEOUT_ERROR_MESSAGE } from "../constants";
import normalizeVideoAnalyticsEventDuration from "../utils/normalizeVideoAnalyticsEventDuration";
import { useVideoAnalytics, VideoAnalyticsEvent } from "./VideoAnalytics";
import { jsx as _jsx } from "react/jsx-runtime";
var CrehanaPlayerWithVideoAnalytics = function CrehanaPlayerWithVideoAnalytics(_ref, ref) {
  var videoId = _ref.videoId,
    onReadyProp = _ref.onReady,
    onPlayProp = _ref.onPlay,
    onPauseProp = _ref.onPause,
    onSeekProp = _ref.onSeek,
    onBackwardProp = _ref.onBackward,
    onForwardProp = _ref.onForward,
    onTimeUpdateProp = _ref.onTimeUpdate,
    onFullscreenProp = _ref.onFullscreen,
    onSpeedChangeProp = _ref.onSpeedChange,
    onQualityChangeProp = _ref.onQualityChange,
    onErrorProp = _ref.onError,
    nextVideo = _ref.nextVideo,
    videoAnalyticsData = _ref.videoAnalyticsData,
    crehanaPlayerProps = _objectWithoutProperties(_ref, _excluded);
  var videoAnalytics = useVideoAnalytics(_objectSpread(_objectSpread({}, videoAnalyticsData), {}, {
    playerElementRef: crehanaPlayerProps.playerRef
  }));
  var firstPlay = useRef(true);
  var isPlaying = useRef(true);
  var videoCompleted = useRef(false);
  var progressStartPosition = useRef(null);
  var lastTimeUpdate = useRef(null);
  var trackProgress = function trackProgress(position, duration, newProgressStart) {
    if (!duration || duration <= 0) {
      return;
    }
    videoAnalytics.track({
      event: {
        name: VideoAnalyticsEvent.PROGRESS,
        position: position,
        duration: normalizeVideoAnalyticsEventDuration(duration)
      }
    });
    progressStartPosition.current = newProgressStart;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    // Discard lastProgress data from the previous video to start over
    progressStartPosition.current = null;
    lastTimeUpdate.current = null;
    firstPlay.current = true;
    videoCompleted.current = false;
    return function () {
      // Check if there was a pending progress event that needs to be tracked
      var progressPosition = progressStartPosition.current;
      var progressDuration = lastTimeUpdate.current && typeof progressPosition === 'number' ? normalizeVideoAnalyticsEventDuration(lastTimeUpdate.current.position - progressPosition) : 0;
      if (progressDuration > 0) {
        videoAnalytics.track({
          event: {
            name: VideoAnalyticsEvent.PROGRESS,
            position: progressPosition || 0,
            duration: progressDuration
          }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);
  var onReady = function onReady(_ref2) {
    var duration = _ref2.duration,
      type = _ref2.type,
      quality = _ref2.quality;
    videoAnalytics.onVideoReady({
      videoType: type,
      videoDuration: duration,
      quality: quality
    });
    if (onReadyProp) {
      onReadyProp({
        duration: duration,
        type: type,
        quality: quality
      });
    }
  };
  var onPlay = function onPlay(position) {
    isPlaying.current = true;
    if (firstPlay.current) {
      videoAnalytics.track({
        event: {
          name: VideoAnalyticsEvent.FIRST_PLAY,
          position: position
        }
      });
      firstPlay.current = false;
    } else {
      videoAnalytics.track({
        event: {
          name: VideoAnalyticsEvent.PLAY,
          position: position
        }
      });
    }
    if (onPlayProp) {
      onPlayProp(position);
    }
  };
  var onPause = function onPause(position, duration) {
    isPlaying.current = false;
    // Check if there is any progress up to this point that needs to be tracked
    var progressPosition = progressStartPosition.current;
    var progressDuration = typeof progressPosition === 'number' ? normalizeVideoAnalyticsEventDuration(position - progressPosition) : 0;
    if (progressDuration > 0) {
      var events = [{
        name: VideoAnalyticsEvent.PROGRESS,
        position: progressPosition || 0,
        duration: progressDuration
      }];

      // Track pause only if it's not fired on video completed
      if (duration - position >= 1) {
        events.push({
          name: VideoAnalyticsEvent.PAUSE,
          position: position
        });
      }
      videoAnalytics.track({
        events: events
      });
      progressStartPosition.current = null;
    } else if (duration - position >= 1) {
      videoAnalytics.track({
        event: {
          name: VideoAnalyticsEvent.PAUSE,
          position: position
        }
      });
    }
    if (onPauseProp) {
      onPauseProp(position, duration);
    }
  };
  var onSeek = function onSeek(position) {
    videoAnalytics.track({
      event: {
        name: VideoAnalyticsEvent.SEEK,
        position: position
      }
    });
    if (onSeekProp) {
      onSeekProp(position);
    }
  };
  var onBackward = function onBackward(position) {
    videoAnalytics.track({
      event: {
        name: VideoAnalyticsEvent.BACK,
        position: position
      }
    });
    if (onBackwardProp) {
      onBackwardProp(position);
    }
  };
  var onForward = function onForward(position) {
    videoAnalytics.track({
      event: {
        name: VideoAnalyticsEvent.FORWARD,
        position: position
      }
    });
    if (onForwardProp) {
      onForwardProp(position);
    }
  };
  var onComplete = function onComplete(position) {
    videoAnalytics.track({
      event: {
        name: VideoAnalyticsEvent.COMPLETE,
        position: position
      }
    });
    videoCompleted.current = true;
  };
  var onTimeUpdate = function onTimeUpdate(_ref3) {
    var currentTime = _ref3.currentTime,
      currentProgress = _ref3.currentProgress,
      duration = _ref3.duration;
    // Ignore progress when a seek is done on pause
    if (!isPlaying.current) {
      return;
    }
    var now = new Date();

    // Set the start progress position if none is found
    if (progressStartPosition.current === null) {
      progressStartPosition.current = currentTime;
    }
    // Check if there was a time update before
    if (lastTimeUpdate.current) {
      if (
      // Check if the current position is placed before or more than a second forward from the position on the last time update
      // (This means there was probably a seek event)
      currentTime < lastTimeUpdate.current.position || currentTime - lastTimeUpdate.current.position > 1) {
        var eventDuration = lastTimeUpdate.current.position - progressStartPosition.current;

        // If a "negative" progress is found (this means the player was seeked backwards), only reset the progressStartPosition
        if (eventDuration <= 0) {
          progressStartPosition.current = null;
        } else {
          // Track event and set the current position as the new progressStart
          trackProgress(progressStartPosition.current, eventDuration, currentTime);
        }
      } else if (
      // Check if the progress duration has reached 10 seconds
      currentTime - progressStartPosition.current >= 10 ||
      // Check if the last time update was fired two or more seconds ago
      // (This means the player was paused or buffering)
      now.getTime() - lastTimeUpdate.current.date.getTime() >= 2000 && currentTime - progressStartPosition.current > 0) {
        // Track event and set the progressStartPosition as null so it's restarted on the next time update
        trackProgress(progressStartPosition.current, currentTime - progressStartPosition.current, null);
      }
    }
    // Set this event data as lastTimeUpdate
    lastTimeUpdate.current = {
      date: now,
      position: currentTime
    };
    // Check if the video was completed
    if (!videoCompleted.current && currentTime >= duration - (videoAnalyticsData.videoEndingDuration || DEFAULT_VIDEO_ENDING_DURATION)) {
      onComplete(currentTime);
    }
    if (onTimeUpdateProp) {
      onTimeUpdateProp({
        currentTime: currentTime,
        currentProgress: currentProgress,
        duration: duration
      });
    }
  };
  var onFullscreen = function onFullscreen(isFullscreen) {
    videoAnalytics.onFullscreenChange(isFullscreen);
    if (onFullscreenProp) {
      onFullscreenProp(isFullscreen);
    }
  };
  var onSpeedChange = function onSpeedChange(speedValue) {
    videoAnalytics.onSpeedChange(speedValue);
    if (onSpeedChangeProp) {
      onSpeedChangeProp(speedValue);
    }
  };
  var onQualityChange = function onQualityChange(quality) {
    videoAnalytics.onQualityChange(quality);
    if (onQualityChangeProp) {
      onQualityChangeProp(quality);
    }
  };
  var onError = function onError(error) {
    if (error === VIDEO_LOAD_TIMEOUT_ERROR_MESSAGE) {
      videoAnalytics.track({
        event: {
          name: VideoAnalyticsEvent.ERROR_TIMEOUT,
          position: 0
        }
      });
    }
    if (onErrorProp) {
      onErrorProp(error);
    }
  };
  return /*#__PURE__*/_jsx(CrehanaPlayerClass, _objectSpread({
    ref: ref,
    videoId: videoId,
    onReady: onReady,
    onPlay: onPlay,
    onPause: onPause,
    onSeek: onSeek,
    onBackward: onBackward,
    onForward: onForward,
    onTimeUpdate: onTimeUpdate,
    onFullscreen: onFullscreen,
    onSpeedChange: onSpeedChange,
    onQualityChange: onQualityChange,
    onError: onError,
    nextVideo: nextVideo ? _objectSpread(_objectSpread({}, nextVideo), {}, {
      play: function play(currentTime) {
        if (!videoCompleted.current) {
          onComplete(currentTime);
        }
        nextVideo.play(currentTime);
      }
    }) : undefined
  }, crehanaPlayerProps));
};
export default /*#__PURE__*/forwardRef(CrehanaPlayerWithVideoAnalytics);